import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useAuthStore } from "stores/auth";

// Actions
import { changeStringField } from "redux/auth/slice";

// Api
import { authApi } from "redux/auth/api";

// Components
import RegistrationForm from "components/molecules/RegistrationForm";

// Constants
import { GET_POPUP_TYPES } from "constants/popupConfig";
import {
  STORAGE_AUTH_TOKEN,
  STORAGE_CLICK_ID,
  STORAGE_UTM_CAMPAIGN,
  STORAGE_UTM_MEDIUM,
  STORAGE_UTM_SOURCE,
} from "constants/variables";

// Selectors
import { userApi } from "redux/user/api";

// Helpers
import { mappedError } from "helpers/errors";

// Hooks
import { useGTM } from "hooks/useGTM";

// Selectors
import * as authSelectors from "redux/auth/selectors";
import { useCatalogsStore } from "stores/catalogs";

// Types
import { RegisterRequestType } from "types/Auth";

// Utils
import { getClientId } from "utils/ga";
import { getLocalStorageItem, setLocalStorageItem } from "utils/localStorage";

// Url
import url from "url/index";
import { useTogglePopup } from "hooks/useTogglePopup";
import ConfirmPasswordBanner from "components/atoms/ConfirmPasswordBanner";
import { useLocalizationStore } from "stores/localization";
// eslint-disable-next-line unused-imports/no-unused-imports
import OAuthBlock from "components/molecules/OAuthBlock";
// eslint-disable-next-line unused-imports/no-unused-imports
import AuthSelector from "components/atoms/AuthSelector";
import axios from "axios";
// eslint-disable-next-line import/namespace
import { AuthMethodsType } from "components/molecules/LoginForm";
import useQueryParam from "hooks/useQueryParam";
import { useCurrency } from "hooks/useCurrency";
import { useAnalytics } from "hooks/useAmplitude";

type RegistrationFormContainerProps = { isAgreementShow?: boolean };

// eslint-disable-next-line unused-imports/no-unused-vars
const options: Array<{ id: AuthMethodsType; label: string }> = [
  { id: "email", label: "Email" },
  { id: "socials", label: "Socials" },
];

const REG_PARAM_MAPPING = {
  "1": "email", // e-mail
  "2": "socials", // social networks
  "3": "phone", // phone number
};

const RegistrationFormContainer = ({
  isAgreementShow,
}: RegistrationFormContainerProps) => {
  const [searchParams] = useSearchParams();
  const [selectedMethod, setSelectedMethod] =
    useState<AuthMethodsType>("email");
  const [availableMethods, setAvailableMethods] = useState<any>([]);
  const [availableMethodsFromUrl, setAvailableMethodsFromUrl] = useState<
    string[]
  >([]);
  const [authOptionsFromUrl, setAuthOptionsFromUrl] = useState<
    Array<{ id: AuthMethodsType; label: string }>
  >([]);
  const [signIn] = authApi.useLoginMutation();
  const methods = useForm({
    defaultValues: {
      ageAgreement: true,
      policyAgreement: true,
      currencyCode: "",
    },
  });
  const [errorResponse, setErrorResponse] = useState("");
  // eslint-disable-next-line unused-imports/no-unused-vars

  const dispatch = useDispatch();
  const { trackEvent, getCampainId, getTimeStamp } = useAnalytics();
  const defaultCurrency = useCurrency();
  const location = useLocation();
  const GTM = useGTM();
  const queryClient = useQueryClient();
  const { watch } = methods;
  // eslint-disable-next-line unused-imports/no-unused-vars
  const formValues = watch();
  const regParamAuth = useQueryParam("reg");
  const token = getLocalStorageItem(STORAGE_AUTH_TOKEN);
  // eslint-disable-next-line  unused-imports/no-unused-vars
  const { refetch, isSuccess } = userApi.useGetProfileQuery(
    token ? "" : skipToken,
  );
  const setIsAfterAuthCheck = useAuthStore(
    (state) => state.setIsAfterAuthCheck,
  );
  const { login, password, currencyCode } = useSelector(authSelectors.all);
  const languages = useCatalogsStore((state) => state.languages);
  const lang = useLocalizationStore((state) => state.language) ?? "EN";
  const [signUp, { isLoading }] = authApi.useRegisterMutation();
  const { openPopup, closePopup } = useTogglePopup();
  const navigate = useNavigate();
  const [telegramAuthError, setTelegramAuthError] = useState("");

  useEffect(() => {
    const regParam = searchParams.get("reg");

    const paramToUse = regParam || "123";

    const methodsList: string[] = [];
    const authOptions: Array<{ id: AuthMethodsType; label: string }> = [];

    for (let i = 0; i < paramToUse.length; i += 1) {
      const digit = paramToUse[i];
      const method = REG_PARAM_MAPPING[digit];

      if (method) {
        methodsList.push(method);

        const existingOption = authOptions.find(
          (option) => option.id === (method as AuthMethodsType),
        );

        if (!existingOption) {
          let optionLabel = "Unknown";
          if (method === "email") {
            optionLabel = "Email";
          } else if (method === "socials") {
            optionLabel = "Socials";
          } else if (method === "phone") {
            optionLabel = "Phone";
          }

          authOptions.push({
            id: method as AuthMethodsType,
            label: optionLabel,
          });
        }
      }
    }

    setAvailableMethodsFromUrl(methodsList);
    setAuthOptionsFromUrl(authOptions);

    if (authOptions.length > 0) {
      setSelectedMethod(authOptions[0].id);
    }
  }, [searchParams]);

  // eslint-disable-next-line unused-imports/no-unused-vars
  const optionsToDisplay =
    authOptionsFromUrl.length > 0 ? authOptionsFromUrl : options;

  // eslint-disable-next-line unused-imports/no-unused-vars
  const shouldShowAuthSelector = authOptionsFromUrl.length > 1;

  useEffect(() => {
    if (token) {
      refetch();
    } else {
      setTimeout(() => {
        setIsAfterAuthCheck(true);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const from = searchParams.get("from");
  const hash = searchParams.get("hash");
  const currencyParam = searchParams.get("currency");

  const didRegister = useRef(false);

  useEffect(() => {
    if (didRegister.current) {
      return;
    }

    // костыль для багa с from=sign-in
    if (from === "sign-in") {
      return;
    }

    if (!hash) {
      return;
    }

    // Проверяем, что есть хотя бы один код валюты:
    const currency = currencyParam || currencyCode || defaultCurrency;

    if (!currency) {
      return;
    }

    // ставим флаг, чтобы не повторяться
    didRegister.current = true;

    const getTelegramToken = async () => {
      try {
        const endpoint = "/v1/auth/register";

        const response = await axios.post(
          endpoint,
          {
            userTelegramData: Object.fromEntries(
              // отфильтровываем popup и from и currency
              Array.from(searchParams.entries()).filter(
                ([key]) => !["popup", "currency", "from"].includes(key),
              ),
            ),
            authType: "telegram",
            currencyCode: currency,
            languageId: 6,
            depositUrl: "https://www.telegram.com/",
            welcomeBonusUrl: "https://www.telegram.com/",
            isTestUser: true,
          },
          {
            headers: { "Content-Type": "application/json" },
          },
        );

        // eslint-disable-next-line no-console
        console.log("response.data", response.data);

        const { login: userEmail, tempPassword, authToken } = response.data;

        if (userEmail && tempPassword) {
          const submitData = await signIn({
            login: userEmail,
            password: tempPassword,
          });

          setLocalStorageItem("socialTempPassword", tempPassword);

          if ("data" in submitData) {
            navigate(
              `?popup=password-registration-confirm&userEmail=${encodeURIComponent(
                userEmail,
              )}`,
            );
          }
        } else if (authToken) {
          setLocalStorageItem(STORAGE_AUTH_TOKEN, authToken);
          navigate("/");
        }
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error("error", error.message);
        setTelegramAuthError(error?.response?.data?.message);
      }
    };

    getTelegramToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, hash, currencyParam, currencyCode, signIn, navigate]);

  useEffect(() => {
    try {
      const getMethods = async () => {
        const response = await axios.get("/v1/auth/available-methods");

        if (response && response?.data) {
          const backendMethods = response?.data?.availableMethods;

          if (availableMethodsFromUrl.length === 0) {
            setAvailableMethods(backendMethods);
          } else {
            const mappedMethods = {
              email: ["password"],
              socials: ["google", "facebook", "telegram", "instagram"],
              phone: ["phone"],
            };

            const allowedMethods = new Set();
            availableMethodsFromUrl.forEach((method) => {
              if (mappedMethods[method]) {
                mappedMethods[method].forEach((m) => allowedMethods.add(m));
              }
            });

            const filteredMethods = backendMethods.filter((method) =>
              allowedMethods.has(method),
            );

            setAvailableMethods(filteredMethods);
          }
        }
      };

      getMethods();
      // eslint-disable-next-line @typescript-eslint/no-shadow
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.log("error", error);
    }
  }, [availableMethodsFromUrl]);

  async function submit(data) {
    try {
      const language =
        (
          languages?.find(
            (l) => l.isoCode.toLowerCase() === lang.toLowerCase(),
          ) || {}
        ).id || 0;
      const clickId = getLocalStorageItem(STORAGE_CLICK_ID);
      const utmMedium = getLocalStorageItem(STORAGE_UTM_MEDIUM);
      const utmSource = getLocalStorageItem(STORAGE_UTM_SOURCE);
      const utmCampaign = getLocalStorageItem(STORAGE_UTM_CAMPAIGN);
      const queryParams = new URLSearchParams(location.search);
      const rParam = queryParams.get("r");
      const urlLocation = window.location.href;

      const params: RegisterRequestType = {
        login: data.login,
        password: data.password,
        currencyCode: data.currencyCode || "",
        languageId: language,
        depositUrl: `${window.location.origin.replace(":3000", ".ru")}/?popup=deposit`,
        welcomeBonusUrl:
          window.location.origin.replace(":3000", ".ru") +
          url.routes.info.welcomeBonus(),
        gaClientId: getClientId(),
      };

      if (clickId) {
        params.clickId = clickId;
      }
      if (utmMedium) {
        params.utmMedium = utmMedium;
      }
      if (utmSource) {
        params.utmSource = utmSource;
      }
      if (utmCampaign) {
        params.utmCampaign = utmCampaign;
      }
      if (rParam) {
        params.r = rParam;
      }

      const response = await signUp(params);

      // eslint-disable-next-line sonarjs/no-collapsible-if
      if ("data" in response) {
        if (response.data && !isLoading) {
          if (response.data.statusCode === 400) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            setErrorResponse(response.data.message);
          } else {
            trackEvent("registration_success", {
              eventPropertyKey: "registration_success",

              // eslint-disable-next-line camelcase
              registration_method: selectedMethod,
              // eslint-disable-next-line camelcase
              registration_timestamp: getTimeStamp(),
              // eslint-disable-next-line camelcase
              campaign_id: getCampainId(),
            });

            queryClient.invalidateQueries({ queryKey: ["banners"] });
            GTM.registrationStatus(
              response.data.id,
              data.login,
              "success",
              "",
              urlLocation,
              clickId,
              utmMedium,
              utmSource,
              utmCampaign,
            );

            GTM.onRegistrationStart();

            if (params.r) {
              // eslint-disable-next-line no-console
              GTM.onRegistrationReferral(
                params.r,
                urlLocation,
                response.data.id,
                data.login,
                data.currencyCode,
              );
            } else {
              GTM.registrationStatus(
                response.data.id,
                data.login,
                "success",
                "",
                urlLocation,
                clickId,
                utmMedium,
                utmSource,
                utmCampaign,
              );
            }
            closePopup();
            openPopup(GET_POPUP_TYPES.popup.deposit);
          }
        }
      }
      // eslint-disable-next-line sonarjs/no-collapsible-if
      if ("error" in response) {
        GTM.registrationStatus(
          data.id,
          data.login,
          "error",
          response?.error.toString(),
          urlLocation,
          clickId,
          utmMedium,
          utmSource,
          utmCampaign,
        );

        GTM.onRegistrationError(response?.error.toString());

        if (response.error) {
          const errors = mappedError(response.error);
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < errors.length; i++) {
            if (errors[i].name) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              methods.setError(errors[i].name, {
                type: "custom",
                message: errors[i].error,
              });
            } else {
              setErrorResponse((prev) => {
                if (prev !== "") {
                  return [...prev.split("; "), errors[i].error].join("; ");
                }
                return errors[i].error;
              });
            }
          }
        }
      }
    } catch (error) {
      const urlLocation = window.location.href;
      const clickId = getLocalStorageItem(STORAGE_CLICK_ID);
      const utmMedium = getLocalStorageItem(STORAGE_UTM_MEDIUM);
      const utmSource = getLocalStorageItem(STORAGE_UTM_SOURCE);
      const utmCampaign = getLocalStorageItem(STORAGE_UTM_CAMPAIGN);

      // eslint-disable-next-line no-console
      console.log("Register Error", error);

      let errorMessage = "Unknown error";
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      trackEvent("form_error", {
        eventPropertyKey: "form_error",
        // eslint-disable-next-line object-shorthand
        errorMessage: errorMessage,
        dataId: data.id,
        dataLogin: data.login,
      });

      GTM.registrationStatus(
        data.id,
        data.login,
        "error",
        errorMessage,
        urlLocation,
        clickId,
        utmMedium,
        utmSource,
        utmCampaign,
      );
    }
  }

  const handleChange = (value: string, { name }: { name: string }) => {
    dispatch(changeStringField({ field: name, value }));

    trackEvent("form_field_filled", {
      // eslint-disable-next-line camelcase
      form_id: name,
      // eslint-disable-next-line camelcase
      field_name: name,
      // eslint-disable-next-line camelcase
      field_value_length: value?.length ?? 0,
    });
  };

  const onSubmit = (data) => {
    submit(data);
  };

  // eslint-disable-next-line unused-imports/no-unused-vars
  const isShowSelector = availableMethods.length > 1;

  return (
    <div>
      {isShowSelector && regParamAuth !== "2" && (
        <div style={{ marginBottom: 20 }}>
          <AuthSelector
            options={optionsToDisplay.filter((option) => option.id !== "phone")}
            selectedMethod={selectedMethod}
            setSelectedMethod={setSelectedMethod}
          />
        </div>
      )}

      {selectedMethod === "email" && (
        <FormProvider {...methods}>
          <RegistrationForm
            login={login}
            password={password}
            error={errorResponse}
            currencyCode={currencyCode}
            isAgreementShow={isAgreementShow}
            onChange={handleChange}
            // eslint-disable-next-line react/jsx-handler-names
            onSubmit={onSubmit}
          />
        </FormProvider>
      )}

      {selectedMethod === "socials" && (
        <div>
          <div style={{ marginTop: 10 }}>
            <ConfirmPasswordBanner />
          </div>

          <OAuthBlock
            availableMethods={availableMethods}
            currencyCode={formValues.currencyCode}
            type="signUp"
          />
        </div>
      )}

      {telegramAuthError && (
        <div
          style={{
            width: "100%",
            color: "red",
            marginTop: 12,
            textAlign: "center",
          }}
        >
          {telegramAuthError}
        </div>
      )}

      {selectedMethod === "phone" && (
        <div>
          <div style={{ marginTop: 10 }}>
            <ConfirmPasswordBanner />
            <div className="placeholder-for-phone-auth">
              {/* Phone authentication component would go here */}
              Phone authentication is not yet implemented
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationFormContainer;
